import { LocationState, DateState, TravelersState } from "stores/wizard/state/global";
import { GlobalConfig } from "stores/wizard/config/staticConfigs/global";
import { observable, computed, action } from "mobx";
import { dateState } from "./date";
import { travelersState } from "./travelers";
import { locationState } from "./location";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { Travelers, TravelersMetadata } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { TravelersFieldConfig } from "stores/wizard/config";
import { InfantSeating } from "src/components/shared/TravelersField";
import { LastSearchData } from "stores/wizard/LastSearchData";

export class GlobalWizardState {
  @observable
  public location: LocationState;

  @observable
  public config: GlobalConfig;

  @observable
  public canTrack?: boolean;

  @observable
  public navigation: {
    defaultLob: string;
    activeLob: string;
  };

  public useDefaultLOB = true;

  @observable
  public date: DateState;

  @observable
  public travelers: TravelersState;

  //initialize to economy
  @observable
  public flightClassCode: string = "e";

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get hotelPackageTravelersMetadata() {
    return travelersMetadata(this.travelers.hotelPackage);
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get defaultLob() {
    return this.navigation.defaultLob;
  }

  @computed
  public get activeLob() {
    return this.navigation.activeLob;
  }

  @action
  public setActiveLob(activeLob: string) {
    this.navigation.activeLob = activeLob;
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.date.start = start;
    this.date.end = end;
  };

  @action
  public updateOriginSelection(selection: TypeaheadSelection) {
    const { term, data } = selection as TypeaheadSelectionProps;
    this.location.origin.value = term;
    this.location.origin.metaData.regionId = data && data.regionId;
    this.location.origin.metaData.ttla =
      data &&
      data.hierarchyInfo &&
      data.hierarchyInfo.airport &&
      data.hierarchyInfo.airport.airportCode;
  }

  @action
  public updateDestinationSelection(selection: TypeaheadSelection) {
    const { term, data } = selection as TypeaheadSelectionProps;
    this.location.destination.value = term;
    this.location.destination.metaData.regionId = data && data.regionId;
    this.location.destination.metaData.ttla =
      data &&
      data.hierarchyInfo &&
      data.hierarchyInfo.airport &&
      data.hierarchyInfo.airport.airportCode;
  }

  @action
  public swapOriginAndDestinationLocations() {
    const tempLocation = this.location.origin;
    this.location.origin = this.location.destination;
    this.location.destination = tempLocation;
  }

  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.location.destination.value = value;
    this.location.destination.metaData.regionId = regionID;
    this.location.destination.metaData.ttla = ttla;
    this.location.destination.metaData.countryCode = countryCode;
    this.location.destination.metaData.latLong = latLong;
  }

  public updateOriginFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.location.origin.value = value;
    this.location.origin.metaData.regionId = regionID;
    this.location.origin.metaData.ttla = ttla;
    this.location.origin.metaData.countryCode = countryCode;
    this.location.origin.metaData.latLong = latLong;
  }

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.travelers.nonHotel.rooms = [travelers.rooms[0]];
    this.travelers.hotel.rooms = travelers.rooms;
    this.travelers.hotelPackage.rooms = travelers.rooms;

    this.travelers.nonHotel.infantSeating = travelers.infantSeating;
    this.travelers.hotel.infantSeating = travelers.infantSeating;
    this.travelers.hotelPackage.infantSeating = travelers.infantSeating;
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.travelers.nonHotel.rooms = [travelers.rooms[0]];
    this.travelers.hotel.rooms[0] = travelers.rooms[0];
    this.travelers.hotelPackage.rooms[0] = travelers.rooms[0];

    this.travelers.nonHotel.infantSeating = travelers.infantSeating;
    this.travelers.hotel.infantSeating = travelers.infantSeating;
    this.travelers.hotelPackage.infantSeating = travelers.infantSeating;
  };

  @action
  public setFlightClass(flightClassCode: string) {
    this.flightClassCode = flightClassCode;
  }

  @action
  public validateOriginField = (): boolean => !!this.location.origin.value;

  @action
  public validateDestinationField = (): boolean => !!this.location.destination.value;

  public validateLessThanNTravelers = (
    { numOfAdults, numOfChildren, numOfInfants }: TravelersMetadata,
    { withInfants, minBookableTravelers, maxBookableTravelers }: TravelersFieldConfig
  ): boolean => {
    const numOfTravelers = numOfAdults + numOfChildren + (withInfants ? numOfInfants : 0);

    return numOfTravelers >= minBookableTravelers && numOfTravelers <= maxBookableTravelers;
  };

  public validateUnattendedInfantInLap = (
    { travelersAgeTwelveOrOlder, numOfInfants }: TravelersMetadata,
    { withInfants }: TravelersFieldConfig,
    infantSeating: InfantSeating
  ): boolean => {
    if (!withInfants || infantSeating === InfantSeating.IN_SEAT) {
      return true;
    }

    return travelersAgeTwelveOrOlder >= numOfInfants;
  };

  public validatePartialDateWithinRange = (
    rangeStart: Date,
    rangeEnd: Date,
    date: Date
  ): boolean => {
    const minValidDate = new Date(
      rangeStart.getFullYear(),
      rangeStart.getMonth(),
      rangeStart.getDate()
    );
    const maxValidDate = new Date(
      rangeEnd.getFullYear(),
      rangeEnd.getMonth(),
      rangeEnd.getDate(),
      23,
      59,
      59,
      999
    );

    return date >= minValidDate && date <= maxValidDate;
  };

  public validateMaxDateRange = (startDate: Date, endDate: Date, maxDaysRange: number): boolean => {
    const maxEndDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate() + maxDaysRange + 1
    );

    return endDate < maxEndDate;
  };

  constructor(config: GlobalConfig, canTrack?: boolean, lastSearchData?: LastSearchData) {
    this.config = config;
    this.navigation = config.navigation;
    this.location = locationState();
    this.date = dateState();
    this.travelers = travelersState;
    this.canTrack = canTrack;
    if (this.canTrack && lastSearchData && lastSearchData.lob) {
      this.navigation.defaultLob = lastSearchData.lob || config.navigation.defaultLob;
    }
  }
}
